import { isMsgraphReturn } from './features/me/msgraphReturn'
import { microsoft365Address } from './features/msgraph/agentAccessLinks'
/* global __BUILD_VERSION__, __APP_COMMIT__ */
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { featureRouteLoaders, settingsTabLoaders } from './platform/navigation/route-loaders.index.generated'
import { composeFrontendRoutes } from './platform/featureModules'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { loginRequest } from './platform/authConfig'
import { Suspense, useState, useEffect, useLayoutEffect, useRef } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { appQueryClient } from './platform/query/queryClient'
import { PermissionProvider } from './platform/context/PermissionContext'
import AccessGate from './platform/components/AccessGate'
import SetupGate from './platform/components/SetupGate'
import IntegrationLimitNotice from './platform/components/IntegrationLimitNotice'
import { hasImpersonationToken as impersonationSessionActive } from './platform/services/impersonationSession'
import { EntitlementProvider, useEntitlements } from './platform/context/EntitlementContext'
import { isCanonicalEntitlementKey } from '@ops/entitlements'
import { ConfirmProvider } from './platform/components/ConfirmDialog'
import { ToastProvider } from './platform/components/ToastProvider'
import { LazyMount } from './platform/ui/lazy'
import { ThemeProvider } from './platform/context/ThemeContext'
import { ViewPreferencesProvider } from './platform/context/ViewPreferencesContext'
import { BrandingProvider, useBranding } from './platform/context/BrandingContext'
import { useTheme } from './platform/context/ThemeContext'
import { TimezoneProvider } from './platform/context/TimezoneContext'
import { AUTH_LOGIN_REQUIRED_EVENT, primeSessionBootstrap } from './platform/services/apiClient'
import GaezlaHeader from './platform/layout/GaezlaHeader'
import { findRouteLoader, lazyRoute, preloadFeatureEntry, preloadSettingsTab } from './platform/navigation/routeLoading'
import { installStaleChunkRecovery } from './platform/navigation/staleChunkRecovery'
import { registerApplianceOptionsLoader, registerHostAgentOptionsLoader, registerIntegrationSummaryLoader, registerOpConnectionsLoader } from './platform/featureHooks'
import useVisibilityRefresh from './platform/hooks/useVisibilityRefresh'
import { GuideTourProvider } from './platform/guide/GuideTourProvider'
const Dashboard = lazyRoute(() => import('./pages/Dashboard'))
const Agents = lazyRoute(() => import('./features/agents/AgentsPage'))
const SettingsV2 = lazyRoute(() => import('./pages/SettingsV2'))
const SharedDoc = lazyRoute(() => import('./features/docs/SharedDoc'))
const License = lazyRoute(() => import('./pages/License'))
const Investigate = lazyRoute(() => import('./features/investigate/cockpit/Cockpit'))
const AIGovernor = lazyRoute(() => import('./features/ai_providers/AIGovernor'))
const AppliancesV2 = lazyRoute(() => import('./pages/AppliancesV2'))
const SectionDashboard = lazyRoute(() => import('./pages/dashboard/SectionDashboard'))
const NotFound = lazyRoute(() => import('./pages/NotFound'))
const GettingStarted = lazyRoute(() => import('./pages/GettingStarted'))
const RightsizingProvider = lazyRoute(() => import('./features/rightsizing/RightsizingContext').then((module) => ({ default: module.RightsizingProvider })))
const FeedbackModal = lazyRoute(() => import('./features/settings/FeedbackModal'))
const NavigationAssistant = lazyRoute(() => import('./features/me/NavigationAssistant'))
import ImpersonationBanner from './platform/components/ImpersonationBanner'

const UserSettings = lazyRoute(() => import('./pages/UserSettings'))

export const STANDALONE_INVESTIGATE_CASE_PATH = '/investigate/:caseId/standalone'

// A standalone case, and the case's browser in a tab of its own
// (BrowserLivePage), get the whole window: no console header or footer.
export function isStandaloneInvestigateCasePath(pathname) {
  return /^\/investigate\/[^/]+\/(standalone|browser)\/?$/.test(pathname)
}

// The application shell owns the sidebar's integration-status composition.
// Register a lazy loader synchronously so a cold start can fetch the summary
// without eagerly importing the Settings route graph. Route modules must not
// be relied on for shell initialization: they are intentionally loaded only
// after navigation by the route-performance architecture.
registerIntegrationSummaryLoader(async () => {
  const { loadIntegrationSummary } = await import('./features/settings/useIntegrationSummary')
  return loadIntegrationSummary()
})

// The Settings reads shared across credential tabs and instance pages
// (settings-shared-primitives): one cached 1Password connection list and
// one slim appliance-options list. Same lazy-loader shape as the summary:
// the shell bundle never eagerly pulls in the settings or queue graphs.
// The appliance picker asks for the server's max page so large estates are
// not truncated at the typeahead default.
registerOpConnectionsLoader(async () => {
  const { listOpConnections } = await import('./features/settings/client')
  const res = await listOpConnections()
  return res.data?.connections ?? []
})
registerApplianceOptionsLoader(async () => {
  const { listAgentOptions } = await import('./features/queue/client')
  const res = await listAgentOptions({ role: 'appliance', limit: 1000 })
  return res.data?.options ?? []
})
registerHostAgentOptionsLoader(async () => {
  const { listAgentOptions } = await import('./features/queue/client')
  const res = await listAgentOptions({ role: 'agent', osType: 'linux', limit: 1000 })
  return res.data?.options ?? []
})

const appVersion = window.__APP_CONFIG__?.appVersion || __BUILD_VERSION__
const loadedAppCommit = window.__APP_CONFIG__?.appCommit || __APP_COMMIT__
installStaleChunkRecovery({ version: loadedAppCommit })

// Deploy freshness guard: a loaded SPA tab keeps running the JS bundle
// it booted with. After a deploy, dist/version.json carries the new
// commit; comparing it to the bundle's compile-time commit tells the
// operator the tab is stale. Both sides must be meaningful commits —
// "local" or empty means we can't reason about freshness, so stay
// silent rather than nag during dev or first-deploy.
export function shouldShowVersionBanner(loadedCommit, remoteCommit) {
  if (!loadedCommit || !remoteCommit) return false
  if (loadedCommit === 'local' || remoteCommit === 'local') return false
  return loadedCommit !== remoteCommit
}

export async function fetchLatestDeployedCommit(fetchImpl = fetch, signal) {
  try {
    const res = await fetchImpl(`/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    })
    if (!res.ok) return null
    const data = await res.json()
    return typeof data?.commit === 'string' ? data.commit : null
  } catch {
    return null
  }
}

function useLatestDeployedCommit({ intervalMs = 60000, initialDelayMs = 5000 } = {}) {
  const [latest, setLatest] = useState(null)
  useVisibilityRefresh({
    refresh: async ({ signal }) => {
      const commit = await fetchLatestDeployedCommit(fetch, signal)
      if (!signal.aborted && commit) setLatest(commit)
    },
    intervalMs,
    initialDelayMs,
    refreshOnFocus: false,
  })
  return latest
}

export function VersionFreshnessBanner({
  loadedCommit,
  latestCommit,
  onReload = () => window.location.reload(),
}) {
  if (!shouldShowVersionBanner(loadedCommit, latestCommit)) return null
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1080,
        /* informational banner: info tint + info ink (AA in both modes) */
        backgroundColor: 'var(--blue-3)',
        color: 'var(--blue-12)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        fontSize: 14,
        borderBottom: '1px solid var(--blue-6)',
      }}
    >
      <span>A new dashboard version is available.</span>
      <button
        type="button"
        onClick={onReload}
        style={{
          backgroundColor: 'transparent',
          color: 'var(--blue-12)',
          border: '1px solid var(--blue-8)',
          borderRadius: 'var(--app-radius)',
          padding: '4px 12px',
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  )
}

function ConnectedVersionFreshnessBanner({ loadedCommit }) {
  const latestCommit = useLatestDeployedCommit()
  return <VersionFreshnessBanner loadedCommit={loadedCommit} latestCommit={latestCommit} />
}

// Public routes that don't require authentication
const PUBLIC_ROUTES = ['/docs/shared']

// An MCP client's consent request arrives as a deep link (/oauth/consent?…)
// that the login redirect would drop: MSAL returns to the origin, not to the
// request URL (navigateToLoginRequestUrl: false). Keep it for main.jsx to
// restore after the redirect completes.
export const OAUTH_CONSENT_RETURN_KEY = 'oauthConsentReturn'
export function stashConsentReturn(location, storage = window.sessionStorage) {
  if (location?.pathname !== '/oauth/consent' && !isMsgraphReturn(location)) return
  try { storage.setItem(OAUTH_CONSENT_RETURN_KEY, `${location.pathname}${location.search || ''}`) } catch { /* private mode */ }
}

// Check if current path is a public route
function isPublicRoute(pathname) {
  return PUBLIC_ROUTES.some(route => pathname.startsWith(route))
}

function isTopLevelWindow(win = window) {
  try {
    return win.self === win.top
  } catch {
    return false
  }
}

export function canStartAuthRedirect({
  pathname,
  hasImpersonationToken,
  inProgress,
  win = window,
}) {
  if (!isTopLevelWindow(win)) return false
  if (hasImpersonationToken) return false
  if (isPublicRoute(pathname || '')) return false
  return !inProgress || inProgress === 'none'
}

// Stingray pattern — 18 dots in diamond/stingray shape, 10 connecting lines
const STINGRAY_DOTS = [
  [0.3024,0.0000,0.0460], //  0 top
  [0.2028,0.0996,0.0462], //  1
  [0.4042,0.0996,0.0461], //  2
  [0.1092,0.1924,0.0461], //  3
  [0.3057,0.1924,0.0459], //  4
  [0.5052,0.1924,0.0459], //  5
  [0.0000,0.3039,0.0459], //  6 leftmost
  [0.1939,0.3039,0.0459], //  7
  [0.4131,0.3039,0.0459], //  8
  [0.6070,0.3039,0.0460], //  9
  [0.8039,0.3039,0.0459], // 10
  [1.0000,0.3039,0.0460], // 11 tip
  [0.1003,0.4086,0.0461], // 12
  [0.3057,0.4094,0.0459], // 13
  [0.5030,0.4086,0.0461], // 14
  [0.2058,0.5141,0.0462], // 15
  [0.4012,0.5141,0.0461], // 16
  [0.3039,0.6040,0.0462], // 17 bottom
]
const STINGRAY_LINES = [[1,2],[3,4],[4,5],[6,7],[8,9],[9,10],[10,11],[12,13],[13,14],[15,16]]
const LINE_SPEEDS = [0.28,0.33,0.26,0.37,0.23,0.30,0.25,0.34,0.28,0.32]
const OUTER_DOTS = [0,6,11,17]

function stingrayColor(nx, alpha = 1) {
  const r = Math.round(93 + (59 - 93) * nx)
  const g = Math.round(196 + (111 - 196) * nx)
  const b = Math.round(227 + (187 - 227) * nx)
  return `rgba(${r},${g},${b},${alpha})`
}

function useStingray(canvasRef) {
  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    const DPR = Math.min(window.devicePixelRatio || 1, 2)
    let W, H, CX, CY, LS, frame = 0, raf

    // Animation state
    const lineSt = STINGRAY_LINES.map((_, i) => ({ t: i / STINGRAY_LINES.length, dir: 1 }))
    const particles = []
    const ambients = Array.from({ length: 22 }, () => ({
      nx: Math.random(), ny: Math.random() * 1.6 - 0.3,
      r: 0.01 + Math.random() * 0.022,
      phase: Math.random() * Math.PI * 2,
      speed: 0.4 + Math.random() * 0.8,
      ox: (Math.random() - 0.5) * 0.18,
      oy: (Math.random() - 0.5) * 0.18,
      alpha: 0.04 + Math.random() * 0.08,
    }))

    function resize() {
      const p = cv.parentElement.getBoundingClientRect()
      W = p.width * DPR; H = p.height * DPR
      cv.width = W; cv.height = H
      cv.style.width = p.width + 'px'; cv.style.height = p.height + 'px'
      LS = Math.min(W, H) * 0.48
      CX = W * 0.5; CY = H * 0.5 - LS * 0.604 * 0.04
    }

    function layout() {
      const lw = LS, lh = lw * 0.604
      return { lox: CX - lw * 0.5, loy: CY - lh * 0.5, s: lw, dr: STINGRAY_DOTS[0][2] * lw }
    }

    function render() {
      frame++
      ctx.clearRect(0, 0, W, H)
      const T = frame / 60
      const lo = layout()
      const dk = document.documentElement.getAttribute('data-coreui-theme') === 'dark'

      // 1. Background
      if (dk) {
        const bg = ctx.createRadialGradient(CX, CY, 0, CX, CY, Math.max(W, H) * 0.75)
        bg.addColorStop(0, 'rgba(5,14,38,1)')
        bg.addColorStop(0.45, 'rgba(3,9,22,1)')
        bg.addColorStop(1, 'rgba(2,5,12,1)')
        ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H)
      } else {
        ctx.fillStyle = '#edf3fb'; ctx.fillRect(0, 0, W, H)
      }

      // 2. Dot grid (light only)
      if (!dk) {
        const gridSpacing = 28 * DPR
        const offX = (W / 2) % gridSpacing, offY = (H / 2) % gridSpacing
        ctx.fillStyle = 'rgba(80,120,200,0.045)'
        for (let x = offX; x < W; x += gridSpacing)
          for (let y = offY; y < H; y += gridSpacing) {
            ctx.beginPath(); ctx.arc(x, y, 1.1 * DPR, 0, Math.PI * 2); ctx.fill()
          }
      }

      // 3. Ambient floating dots
      ambients.forEach(a => {
        const px = lo.lox + (a.nx + a.ox * Math.sin(T * a.speed * 0.7 + a.phase)) * lo.s
        const py = lo.loy + (a.ny + a.oy * Math.cos(T * a.speed * 0.5 + a.phase)) * lo.s
        const breathe = Math.sin(T * a.speed + a.phase) * 0.5 + 0.5
        const r = a.r * lo.s * (0.8 + breathe * 0.4)
        const alpha = a.alpha * (dk ? 1 : 0.30) * (0.5 + breathe * 0.5)
        ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2)
        ctx.fillStyle = stingrayColor(a.nx, alpha)
        ctx.fill()
      })

      // 4. Glow bloom (dark only)
      if (dk) {
        const bloom = ctx.createRadialGradient(CX, CY, lo.s * 0.1, CX, CY, lo.s * 0.85)
        bloom.addColorStop(0, 'rgba(30,80,160,0.22)')
        bloom.addColorStop(0.5, 'rgba(15,50,120,0.10)')
        bloom.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.beginPath(); ctx.arc(CX, CY, lo.s * 0.85, 0, Math.PI * 2)
        ctx.fillStyle = bloom; ctx.fill()
      }

      // 5. Connecting lines
      ctx.lineCap = 'round'
      ctx.lineWidth = lo.dr * 0.76
      STINGRAY_LINES.forEach(([a, b]) => {
        const da = STINGRAY_DOTS[a], db = STINGRAY_DOTS[b]
        const ax = lo.lox + da[0] * lo.s, ay = lo.loy + da[1] * lo.s
        const bx = lo.lox + db[0] * lo.s, by = lo.loy + db[1] * lo.s
        const grd = ctx.createLinearGradient(ax, 0, bx, 0)
        const a0 = dk ? 0.70 : 0.88
        grd.addColorStop(0, stingrayColor(da[0], a0))
        grd.addColorStop(1, stingrayColor(db[0], a0))
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by)
        ctx.strokeStyle = grd; ctx.stroke()
      })

      // 6. Traveling dots on lines
      STINGRAY_LINES.forEach(([a, b], i) => {
        const st = lineSt[i]
        st.t += LINE_SPEEDS[i] * 0.016 * st.dir
        if (st.t >= 1) { st.t = 1; st.dir = -1 } else if (st.t <= 0) { st.t = 0; st.dir = 1 }

        const da = STINGRAY_DOTS[a], db = STINGRAY_DOTS[b]
        const ax = lo.lox + da[0] * lo.s, ay = lo.loy + da[1] * lo.s
        const bx = lo.lox + db[0] * lo.s, by = lo.loy + db[1] * lo.s
        const px = ax + (bx - ax) * st.t, py = ay + (by - ay) * st.t
        const nx = da[0] + (db[0] - da[0]) * st.t

        if (dk) {
          const gr = ctx.createRadialGradient(px, py, 0, px, py, lo.dr * 2.4)
          gr.addColorStop(0, stingrayColor(nx, 0.72)); gr.addColorStop(1, 'rgba(0,0,0,0)')
          ctx.beginPath(); ctx.arc(px, py, lo.dr * 2.4, 0, Math.PI * 2); ctx.fillStyle = gr; ctx.fill()
          ctx.beginPath(); ctx.arc(px, py, lo.dr * 0.42, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(240,252,255,0.97)'; ctx.fill()
        } else {
          ctx.beginPath(); ctx.arc(px, py, lo.dr * 0.42, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(255,255,255,0.97)'; ctx.fill()
        }
      })

      // 7. Logo dots
      STINGRAY_DOTS.forEach(([nx, ny, nr], i) => {
        const px = lo.lox + nx * lo.s, py = lo.loy + ny * lo.s, pr = nr * lo.s
        const isOuter = OUTER_DOTS.includes(i)
        const pulse = isOuter ? (Math.sin(T * 1.7 + i * 1.05) * 0.5 + 0.5) : 0

        if (dk) {
          const glow = ctx.createRadialGradient(px, py, 0, px, py, pr * (2.6 + pulse * 1.1))
          glow.addColorStop(0, stingrayColor(nx, 0.28 + pulse * 0.18)); glow.addColorStop(1, 'rgba(0,0,0,0)')
          ctx.beginPath(); ctx.arc(px, py, pr * (2.6 + pulse * 1.1), 0, Math.PI * 2); ctx.fillStyle = glow; ctx.fill()
        }

        const dotA = dk ? (0.82 + pulse * 0.12) : (0.92 + pulse * 0.06)
        ctx.beginPath(); ctx.arc(px, py, pr * (1 + pulse * 0.04), 0, Math.PI * 2)
        ctx.fillStyle = stingrayColor(nx, dotA); ctx.fill()

        if (!dk) {
          ctx.beginPath(); ctx.arc(px, py, pr * (1 + pulse * 0.04), 0, Math.PI * 2)
          ctx.strokeStyle = stingrayColor(nx, 0.25); ctx.lineWidth = 0.8 * DPR; ctx.stroke()
        }

        // Specular highlight
        ctx.beginPath(); ctx.arc(px - pr * 0.2, py - pr * 0.22, pr * 0.32, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255,255,255,${dk ? 0.14 : 0.55})`; ctx.fill()

        // Pulse ring on outer dots
        if (isOuter) {
          const ringR = pr * (1.55 + pulse * 1.15)
          ctx.beginPath(); ctx.arc(px, py, ringR, 0, Math.PI * 2)
          ctx.strokeStyle = stingrayColor(nx, dk ? (0.08 + pulse * 0.24) : (0.14 + pulse * 0.20))
          ctx.lineWidth = 0.9 * DPR; ctx.stroke()
        }
      })

      // 8. Concentric pulse rings from logo centre
      for (let i = 0; i < 3; i++) {
        const ph = (T * 0.38 + i / 3) % 1
        const ringAlpha = dk ? (1 - ph) * 0.060 : (1 - ph) * 0.055
        ctx.beginPath(); ctx.arc(CX, CY, lo.s * (0.26 + ph * 0.58), 0, Math.PI * 2)
        ctx.strokeStyle = dk ? `rgba(60,150,240,${ringAlpha})` : `rgba(35,105,220,${ringAlpha})`
        ctx.lineWidth = 1.4 * DPR; ctx.stroke()
      }

      // 9. Particles from outer dots
      if (frame % 15 === 0) {
        const srcs = OUTER_DOTS
        const s = srcs[Math.floor(frame / 15) % 4]
        const [nx, ny] = STINGRAY_DOTS[s]
        const px = lo.lox + nx * lo.s, py = lo.loy + ny * lo.s
        const dx = px - CX, dy = py - CY, len = Math.sqrt(dx * dx + dy * dy) || 1
        const spd = (0.7 + Math.random() * 0.6) * DPR
        particles.push({ x: px, y: py, vx: dx / len * spd, vy: dy / len * spd, nx, life: 1, decay: 1 / (60 + Math.random() * 50) })
      }
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.x += p.vx; p.y += p.vy; p.life -= p.decay
        if (p.life <= 0) { particles.splice(i, 1); continue }
        ctx.beginPath(); ctx.arc(p.x, p.y, 1.3 * DPR, 0, Math.PI * 2)
        ctx.fillStyle = stingrayColor(p.nx, p.life * (dk ? 0.72 : 0.65)); ctx.fill()
      }

      raf = requestAnimationFrame(render)
    }

    window.addEventListener('resize', resize)
    resize(); render()
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf) }
  }, [canvasRef])
}

// Login page component — split layout with stingray pattern + auth panel
function LoginPage({ onLogin }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const stingrayRef = useRef(null)
  const branding = useBranding()
  const { theme, toggleTheme } = useTheme()
  useStingray(stingrayRef)

  const handleClick = async () => {
    setLoading(true)
    setError(null)
    try {
      await onLogin()
    } catch (err) {
      setError('Sign-in failed. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="login-layout">
      <button
        className="login-theme-toggle"
        onClick={toggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </button>
      {/* Left panel — stingray pattern + branding */}
      <div className="login-left">
        <canvas ref={stingrayRef} className="login-globe" />
        <div className="login-brand">
          <img src="/logo.png" alt={`${branding.name} ${branding.subtitle}`} height={36} />
          <div className="login-brand-text">
            {branding.name}
            <small>{branding.subtitle}</small>
          </div>
        </div>
        <div className="login-copy">
          <h2 dangerouslySetInnerHTML={{ __html: branding.tagline }} />
          <p>{branding.taglineSub}</p>
        </div>
      </div>

      {/* Right panel — auth */}
      <div className="login-right">
        <div className="login-auth">
          <div className="login-section-label">Operator Access</div>
          <div className="login-auth-title" dangerouslySetInnerHTML={{ __html: branding.loginHeading }} />
          <p className="login-auth-sub">{branding.loginSub}</p>
          {error && <p className="login-error" role="alert">{error}</p>}
          <button className="login-btn-ms" onClick={handleClick} disabled={loading}>
            {loading ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="2" strokeLinecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur=".8s" repeatCount="indefinite" />
                  </path>
                </svg>
                Redirecting…
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Sign In
              </>
            )}
          </button>
          <div className="login-footer">
            <span className="login-footer-version">v{appVersion}</span>
            <span className="login-live">
              <span className="login-live-dot" />
              Live
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

function FeatureDisabledState({ feature }) {
  return (
    <div className="container mt-3">
      <div className="alert alert-warning mb-0">
        This feature is currently disabled for your customer license.
        {feature ? ` (${feature})` : ''}
      </div>
    </div>
  )
}

function UnknownEntitlementKeyState({ feature }) {
  // A non-canonical `feature` passed to FeatureRoute is a product bug: a
  // typo, a renamed key, or a dropped registry entry. Show a loud error
  // state instead of silently hiding the surface, and surface the detail
  // in the console for dev/CI.
  console.error(`[FeatureRoute] non-canonical entitlement key "${feature}"`)
  return (
    <div className="container mt-3">
      <div className="alert alert-danger mb-0">
        <strong>Configuration error:</strong>{' '}
        route gate references unknown entitlement key
        {feature ? ` "${feature}"` : ''}. This is a product-side bug — the
        canonical entitlement registry does not declare this key.
      </div>
    </div>
  )
}

/**
 * Microsoft consent return (and any old link) to /settings/integrations.
 * The console now asks the admin to return consents to /microsoft-365
 * itself; this keeps consents started before that — and any bookmark —
 * landing on Integrations → Microsoft 365 with their parameters
 * (agent_area_consent, msgraph_consent, area, detail, description, byob).
 */
export function ConsentReturnToGraphSettings() {
  const { search } = useLocation()
  return <Navigate to={microsoft365Address(search)} replace />
}

export function LegacySettingsRedirect() {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/settings', search, hash }} replace />
}

/** Param-preserving redirect for renamed route families (e.g. /cockpit → /investigate). */
function RedirectWithParams({ to }) {
  const params = useParams()
  return <Navigate to={to(params)} replace />
}

export function FeatureRoute({ feature, children }) {
  const { loading, isFeatureEnabled } = useEntitlements()

  if (feature && !isCanonicalEntitlementKey(feature)) {
    return <UnknownEntitlementKeyState feature={feature} />
  }
  if (loading) {
    return (
      <div className="ops-view-state" role="status" aria-live="polite">
        <span className="ops-view-state__spinner" aria-hidden="true" />
        <div><strong>Checking workspace availability</strong><p>Reading the current customer entitlement state.</p></div>
      </div>
    )
  }
  if (!isFeatureEnabled(feature)) {
    return <FeatureDisabledState feature={feature} />
  }
  return children
}

// The License page is gated by `show_license_page` — a top-level snapshot
// flag (PR #55), an admin per-customer display preference. It is NOT an
// entitlement, so it does not go through FeatureRoute / isFeatureEnabled.
export function LicensePageRoute({ children }) {
  const { loading, showLicensePage } = useEntitlements()

  if (loading) {
    return (
      <div className="ops-view-state" role="status" aria-live="polite">
        <span className="ops-view-state__spinner" aria-hidden="true" />
        <div><strong>Checking page availability</strong><p>Reading the current customer display preference.</p></div>
      </div>
    )
  }
  // Only an explicit `false` hides the page — absent/undefined defaults
  // to shown (the License page is a display preference, not fail-closed).
  if (showLicensePage === false) {
    return (
      <div className="container mt-3">
        <div className="alert alert-warning mb-0">
          The License page is hidden for this customer.
        </div>
      </div>
    )
  }
  return children
}

export const legacyRouteLoaders = [
  { path: '/', component: Dashboard },
  { path: '/dashboard/:section', component: SectionDashboard },
  { path: '/onboarding', component: GettingStarted },
  { path: '/investigate', component: Investigate },
  { path: '/investigate/history', component: Investigate },
  { path: STANDALONE_INVESTIGATE_CASE_PATH, component: Investigate },
  { path: '/investigate/:caseId', component: Investigate },
  { path: '/appliances', component: AppliancesV2 },
  { path: '/agents', component: Agents },
  { path: '/ai-governor', component: AIGovernor },
  { path: '/me/settings', component: UserSettings },
  { path: '/settings', component: SettingsV2 },
  { path: '/license', component: License },
  { path: '/docs/shared', component: SharedDoc },
  { path: '*', component: NotFound },
]

export function preloadCurrentRouteAssets(pathname, search = '') {
  const featureEntry = findRouteLoader(featureRouteLoaders, pathname)
  const work = featureEntry ? [preloadFeatureEntry(featureEntry)] : []

  const legacyEntry = findRouteLoader(legacyRouteLoaders, pathname)
  if (legacyEntry?.component?.preload) work.push(legacyEntry.component.preload())

  if (pathname === '/rightsizing' || pathname === '/ai-rightsizing') {
    work.push(RightsizingProvider.preload())
  }

  if (pathname === '/settings' || false) {
    const tab = new URLSearchParams(search).get('tab') || 'general'
    const tabEntry = settingsTabLoaders.find((entry) => entry.tab === tab)
    if (tabEntry) work.push(preloadSettingsTab(tabEntry))
  }

  // Cockpit, GitRepositories, Rightsizing and Docs need the MCP SDK after load: fetch it in
  // parallel with the page chunk, never in the entry chunk.
  if (pathname === '/investigate' || pathname.startsWith('/investigate/') || pathname === '/git-repositories' || pathname === '/rightsizing' || pathname === '/ai-rightsizing' || pathname === '/docs') {
    work.push(import('./platform/services/mcpClient').then((module) => module.prefetchMcpSdk()).catch(() => null))
  }

  return Promise.all(work)
}

const featureRouteComponents = new Map()
function featureRouteComponent(entry) {
  let Component = featureRouteComponents.get(entry.path)
  if (Component) return Component
  Component = lazyRoute(async () => {
    const { feature } = await entry.load()
    const route = composeFrontendRoutes([feature], {
      wrapGate: (key, element) => <FeatureRoute feature={key}>{element}</FeatureRoute>,
    }).find((candidate) => candidate.props.path === entry.path)
    if (!route) throw new Error(`feature route loader is stale: ${entry.module}:${entry.path}`)
    return {
      default: function LoadedFeatureRoute() {
        return route.props.element
      },
    }
  })
  featureRouteComponents.set(entry.path, Component)
  return Component
}

// Authenticated app content. The generated registry is metadata plus dynamic
// import functions only, so composing the route table cannot pull feature code
// into the application entry.
let _composedFeatureRoutes = null
function composedFeatureRoutes() {
  if (!_composedFeatureRoutes) {
    _composedFeatureRoutes = featureRouteLoaders.map((entry) => {
      const Component = featureRouteComponent(entry)
      return <Route key={entry.path} path={entry.path} element={<Component />} />
    })
  }
  return _composedFeatureRoutes
}

// The real route table, exported so the request-budget harness renders every
// route through the same <Routes> the shell renders. No behaviour change:
// AuthenticatedApp renders <AppRoutes /> where the table used to be inline.
export function AppRoutes() {
  return (
    <Routes>
              {/* Feature-module composition (design v7): routes exported by
                  migrated feature modules mount here; unmigrated routes stay
                  in the hand-written table below (strangler). */}
              {composedFeatureRoutes()}
              {/* Dashboard (accessible via logo click) */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard/:section" element={<SectionDashboard />} />
              <Route path="/onboarding" element={<GettingStarted />} />

              {/* OPERATIONS */}
              {/* Investigate = the chat-first case surface (built as "cockpit",
                  a placeholder name). It owns /investigate now; the old V8
                  retired workbench routes are no longer registered.
                  /cockpit/* redirects here — the worker briefly minted those
                  links while the placeholder route was live. */}
              <Route path="/investigate" element={<FeatureRoute feature="investigate"><Investigate /></FeatureRoute>} />
              {/* History = the searchable case archive, demoted to a nav submenu
                  item (sidebar) rather than an in-page button. Static segment, so
                  it ranks ahead of /:caseId and never resolves as a case id. */}
              <Route path="/investigate/history" element={<FeatureRoute feature="investigate"><Investigate /></FeatureRoute>} />
              <Route path={STANDALONE_INVESTIGATE_CASE_PATH} element={<FeatureRoute feature="investigate"><Investigate standalone /></FeatureRoute>} />
              <Route path="/investigate/:caseId" element={<FeatureRoute feature="investigate"><Investigate /></FeatureRoute>} />
              <Route path="/cockpit" element={<Navigate to="/investigate" replace />} />
              <Route path="/cockpit/:caseId" element={<RedirectWithParams to={(p) => `/investigate/${p.caseId}`} />} />
              <Route path="/cockpit/:caseId/diagrams/:diagramId" element={<RedirectWithParams to={(p) => `/investigate/${p.caseId}/diagrams/${p.diagramId}`} />} />
              {/* V7 investigate (/investigate/cases, /investigate/case/:id) and the
                  pre-V8 monitoring-query UI (/investigate/legacy*) are removed —
                  old links land on the V8 workbench via the catch-all below. */}
              <Route path="/investigate/cases" element={<FeatureRoute feature="investigate"><Navigate to="/investigate" replace /></FeatureRoute>} />
              <Route path="/investigate/case/:caseId" element={<FeatureRoute feature="investigate"><Navigate to="/investigate" replace /></FeatureRoute>} />
              <Route path="/investigate/legacy" element={<FeatureRoute feature="investigate"><Navigate to="/investigate" replace /></FeatureRoute>} />
              <Route path="/investigate/legacy/:queryId" element={<FeatureRoute feature="investigate"><Navigate to="/investigate" replace /></FeatureRoute>} />

              {/* APPLIANCES */}
              <Route path="/appliances" element={<FeatureRoute feature="agents"><AppliancesV2 /></FeatureRoute>} />
              <Route path="/appliances/v2" element={<FeatureRoute feature="agents"><Navigate to="/appliances" replace /></FeatureRoute>} />
              <Route path="/appliances/legacy" element={<FeatureRoute feature="agents"><Navigate to="/appliances" replace /></FeatureRoute>} />
              <Route path="/roles" element={<FeatureRoute feature="appliance_config"><Navigate to="/appliances" replace /></FeatureRoute>} />
              <Route path="/appliance-config" element={<FeatureRoute feature="appliance_config"><Navigate to="/appliances" replace /></FeatureRoute>} />

              {/* INTEGRATIONS */}
              {/* /checkmk is a composed integration page (features/checkmk). */}

              {/* EXECUTION */}
              <Route path="/agents" element={<FeatureRoute feature="agents"><Agents /></FeatureRoute>} />

              {/* SYSTEM */}
              {/* AI Governor: top-level operator console. Page enforces
                  Settings.Read client-side (access notice) + server-side;
                  no entitlement gate (it is a permission, not a feature). */}
              <Route path="/ai-governor" element={<AIGovernor />} />
              <Route path="/me/settings" element={<Suspense fallback={<div className="ops-view-state" role="status">Loading personal settings…</div>}><UserSettings /></Suspense>} />
              <Route path="/settings" element={<FeatureRoute feature="settings"><SettingsV2 /></FeatureRoute>} />
              <Route path="/settings/v2" element={<FeatureRoute feature="settings"><Navigate to="/settings" replace /></FeatureRoute>} />
              <Route path="/settings/integrations" element={<FeatureRoute feature="settings"><ConsentReturnToGraphSettings /></FeatureRoute>} />
              <Route path="/settings/legacy" element={<LegacySettingsRedirect />} />
              <Route path="/license" element={<LicensePageRoute><License /></LicensePageRoute>} />

              {/* Redirects for old URLs (bookmarks, links) */}
              <Route path="/monitoring-query" element={<FeatureRoute feature="investigate"><Navigate to="/investigate" replace /></FeatureRoute>} />
              <Route path="/vpcs" element={<FeatureRoute feature="agents"><Navigate to="/appliances" replace /></FeatureRoute>} />
              <Route path="/tunnels" element={<FeatureRoute feature="agents"><Navigate to="/appliances" replace /></FeatureRoute>} />
              <Route path="/ai-tools" element={<FeatureRoute feature="settings"><Navigate to="/settings" replace /></FeatureRoute>} />
              <Route path="/security/appliances" element={<FeatureRoute feature="agents"><Navigate to="/appliances" replace /></FeatureRoute>} />
              <Route path="/licensing" element={<Navigate to="/license" replace />} />

              {/* 404 catch-all */}
              <Route path="*" element={<NotFound />} />
              {/* Public shared-document route. AppRouter renders it without
                  authentication, so it never matches inside AuthenticatedApp;
                  it lives here so the budget harness covers it through the
                  same table. */}
              <Route path="/docs/shared" element={<SharedDoc />} />
    </Routes>
  )
}

export function AuthenticatedApp({ queryClient = appQueryClient } = {}) {
  const { accounts } = useMsal()
  const location = useLocation()
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const branding = useBranding()
  const standaloneCase = isStandaloneInvestigateCasePath(location.pathname)
  const bootstrapSessionKey = impersonationSessionActive()
    ? 'impersonation'
    : accounts?.[0]?.homeAccountId || accounts?.[0]?.localAccountId || 'authenticated'
  // Layout timing guarantees the requests start before the gate providers'
  // passive effects. The API service coalesces StrictMode and later consumers.
  useLayoutEffect(() => {
    void preloadCurrentRouteAssets(location.pathname, location.search).catch(() => {})
  }, [location.pathname, location.search])
  // The session bootstrap is keyed by session, not by page: re-priming on
  // every navigation re-fires the gate reads, so prime only when the
  // session key changes.
  useLayoutEffect(() => {
    void primeSessionBootstrap(bootstrapSessionKey)
  }, [bootstrapSessionKey])
  return (
    <QueryClientProvider client={queryClient}>
    <PermissionProvider>
    <AccessGate>
    <ToastProvider>
    <ImpersonationBanner />
    <SetupGate>
    <EntitlementProvider>
    <TimezoneProvider>
    <RouteScopedRightsizingProvider>
    <ConfirmProvider>
    {/* Guide's official tours: the running tour, its panel and highlight (platform/guide). */}
    <GuideTourProvider>
      <ConnectedVersionFreshnessBanner loadedCommit={loadedAppCommit} />
      <IntegrationLimitNotice />
      <a className="ops-skip-link" href="#ops-main">Skip to workspace</a>
      <div className={`wrapper min-vh-100 ops-wrapper${standaloneCase ? ' ops-wrapper--standalone' : ''}`}>
        <div className="wrapper-content">
          {!standaloneCase && <GaezlaHeader assistant={NavigationAssistant} />}
          <main id="ops-main" className={`main-content ops-main${standaloneCase ? ' ops-main--standalone' : ''}`} tabIndex={-1}>
            <Suspense fallback={<div className="ops-view-state" role="status">Loading workspace…</div>}>
            <AppRoutes />
            </Suspense>
          </main>

          {!standaloneCase && (
            <>
              <footer className="ops-footer">
                <span>{branding.name} {branding.subtitle}</span>
                <span><button type="button" onClick={() => setFeedbackOpen(true)}>Report issue or request feature</button> · v{appVersion}</span>
              </footer>
              {/* Mounted only when opened: rendering the lazy chunk closed on
                  every page holds the first shell commit on its download. The
                  Suspense is required — opening the modal is a sync update. */}
              <LazyMount active={feedbackOpen}>
                <Suspense fallback={null}>
                  <FeedbackModal visible={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
                </Suspense>
              </LazyMount>
            </>
          )}
        </div>
      </div>
    </GuideTourProvider>
    </ConfirmProvider>
    </RouteScopedRightsizingProvider>
    </TimezoneProvider>
    </EntitlementProvider>
    </SetupGate>
    </ToastProvider>
    </AccessGate>
    </PermissionProvider>
    </QueryClientProvider>
  )
}

export function RouteScopedRightsizingProvider({ children }) {
  const { pathname } = useLocation()
  if (pathname !== '/rightsizing' && pathname !== '/ai-rightsizing') return children
  return (
    <Suspense fallback={<div className="ops-view-state" role="status">Loading workspace…</div>}>
      <RightsizingProvider>{children}</RightsizingProvider>
    </Suspense>
  )
}

// Main router component that handles public vs authenticated routes
function AppRouter() {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const location = useLocation()
  const loginRedirectInFlightRef = useRef(false)
  // Impersonation sessions skip MSAL login — the operator is logged in
  // elsewhere; here we use the admin-minted token from sessionStorage
  // (captured in main.jsx) as the sole credential. Same predicate as
  // PermissionContext — single helper, never re-derive.
  const hasImpersonationToken = impersonationSessionActive()

  const handleLogin = async () => {
    try {
      stashConsentReturn(location)
      await instance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  useEffect(() => {
    const handleAuthLoginRequired = () => {
      if (loginRedirectInFlightRef.current) return
      if (!canStartAuthRedirect({
        pathname: location.pathname,
        hasImpersonationToken,
        inProgress,
      })) return

      loginRedirectInFlightRef.current = true
      stashConsentReturn(location)
      instance.loginRedirect(loginRequest).catch((error) => {
        loginRedirectInFlightRef.current = false
        console.error('Login failed:', error)
      })
    }

    window.addEventListener(AUTH_LOGIN_REQUIRED_EVENT, handleAuthLoginRequired)
    return () => {
      window.removeEventListener(AUTH_LOGIN_REQUIRED_EVENT, handleAuthLoginRequired)
    }
  }, [instance, location.pathname, hasImpersonationToken, inProgress])

  // Public routes are accessible without authentication
  if (isPublicRoute(location.pathname)) {
    return (
      <Routes>
        <Route path="/docs/shared" element={<SharedDoc />} />
      </Routes>
    )
  }

  // Show login page if not authenticated and not impersonating
  if (!isAuthenticated && !hasImpersonationToken) {
    return <LoginPage onLogin={handleLogin} />
  }

  return <AuthenticatedApp />
}

function App() {
  return (
    <ThemeProvider>
      <ViewPreferencesProvider>
        <BrandingProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </BrandingProvider>
      </ViewPreferencesProvider>
    </ThemeProvider>
  )
}

export default App
